import { ArrowRight } from 'lucide-react'
import { useLanguage } from '@afsa/shared'
import { useNavigate } from 'react-router-dom'
import { type WebPageSection } from '@/lib/api'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { stripHtml } from '@/utils/helpers/html'
import { Heading, Text } from '@/components/ui/Typography'


interface FeatureCardProps {
  image: string
  title: string
  description: string
  linkText?: string
  onLearnMore?: () => void
}

const FeatureCard = ({ image, title, description, linkText = "Learn More", onLearnMore }: FeatureCardProps) => (
  <motion.article
    className="bg-gray-50 hover:bg-wine-200 group cursor-pointer overflow-hidden p-4 xl:p-6 inline-flex justify-center items-start gap-2 transition-all h-full duration-300"
    whileHover={{ y: -6 }}
    transition={{ type: 'spring', stiffness: 280, damping: 24 }}
    onClick={onLearnMore}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onLearnMore?.()
      }
    }}
    aria-label={`Learn more about ${title}`}
  >
    <div className="inline-flex flex-col h-full justify-start items-start gap-4 xl:gap-6">
      <img
        src={image}
        alt={title}
        className="w-full object-cover aspect-video"
      />
      <div className="space-y-4">
        <Heading level={4} className="  group-hover:text-white  justify-start transition-colors duration-300">
          {title}
        </Heading>
        <Text className=" group-hover:text-white justify-start transition-colors duration-300 line-clamp-3">
          {stripHtml(description)}
        </Text>
      </div>
      <div
        className="inline-flex items-center mt-auto text-sm font-medium font-satoshi text-gray-900 group-hover:text-white transition-colors duration-300"
        onClick={(e) => {
          e.stopPropagation()
          onLearnMore?.()
        }}
      >
        {linkText}
        <ArrowRight className="ml-1 h-6 w-6 md:h-4 md:w-4 group-hover:translate-x-1 transition-transform duration-300" />
      </div>
    </div>
  </motion.article>
)

interface FeaturesSectionProps {
  className?: string;
  data: {
    [key: string]: WebPageSection;
  }
}

const FeaturesSection = ({ className, data }: FeaturesSectionProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()

  const features = [
    {
      title: data?.HOME_HISTORICAL_TIMELINE?.title || t('historical_timeline'),
      description: data?.HOME_HISTORICAL_TIMELINE?.description || t('historical_timeline_desc'),
      image: data?.HOME_HISTORICAL_TIMELINE?.fileUrl || 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/436e364d5664edaf65624f6162f6c339235305d5_1_q2hero.png',
      route: '/about/history'
    },
    {
      title: data?.HOME_LEADERSHIP?.title || t('leadership_title'),
      description: data?.HOME_LEADERSHIP?.description || t('leadership_desc'),
      image: data?.HOME_LEADERSHIP?.fileUrl || 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760297/0419d7b9498659c65e546b484c3a84dbb59d1976_2_grc2la.png',
      route: '/about/leadership'
    },
    {
      title: data?.HOME_MEMBER_COUNTRIES?.title || t('member_countries'),
      description: data?.HOME_MEMBER_COUNTRIES?.description || t('member_countries_desc'),
      image: data?.HOME_MEMBER_COUNTRIES?.fileUrl || 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755760296/93411e5e11cca7d6af1a48e1d3597d83bea53c12_1_nbhkmv.png',
      route: '/about/member-countries'
    }
  ]

  const handleLearnMore = (route: string) => {
    navigate(route)
  }

  return (
    <section className={`py-16 ${className}`} aria-labelledby="features-title">
      <div className=" max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">


        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportReveal}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={fadeUp}>
              <FeatureCard
                title={feature.title}
                description={feature.description}
                image={feature.image}
                linkText={t('learn_more')}
                onLearnMore={() => handleLearnMore(feature.route)}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default FeaturesSection